import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useForm, useStore } from "@tanstack/react-form";
import { zodValidator } from "@tanstack/zod-form-adapter";
import { quotationDetailSchema, type QuotationDetailFormData } from "../../../shared/schemas/quotation.schema";
import { ComboboxConfigMaster } from "@/components/shared/ComboboxConfigMaster";
import { SharedCombobox } from "@/components/shared/SharedCombobox";
import { NumericInput } from "@/components/shared/NumericInput";
import { Switch } from "@/components/ui/switch";
import { TanstackFieldWrapper, TanstackFormControl } from "@/components/ui/tanstack-form";
import { Save, X } from "lucide-react";
import { EditableCombobox } from "@/components/shared/EditableCombobox";
import { formatProductSpecs } from "@/lib/format-specs";
import { focusFirstError } from "../../../lib/form/formUtils";

interface QuotationDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: QuotationDetailFormData) => void;
  initialData?: QuotationDetailFormData | null;
  currency: string;
  customerId?: string;
}

export function QuotationDetailModal({
  isOpen,
  onClose,
  onSave,
  initialData,
  currency,
  customerId,
}: QuotationDetailModalProps) {
  const form = useForm({
    defaultValues: initialData || {
      productName: "",
      quantity: 1,
      priceVND: 0,
      quotedPrice: 0,
      specs: "",
      unit: "Cái",
      notes: "",
      isCombo: false,
      isCommercial: false,
    } as any,
    // @ts-ignore
    validatorAdapter: zodValidator(),
    validators: { onChange: quotationDetailSchema as any },
    onSubmit: async ({ value }) => {
      onSave(value as QuotationDetailFormData);
      onClose();
    },
  });

  const isCommercialValue = useStore(form.store, (state: any) => state.values.isCommercial);

  if (!isOpen) return null;

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    form.handleSubmit();
    requestAnimationFrame(() => focusFirstError(form.state as any));
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent 
        className="max-w-[95vw] sm:max-w-[1000px] w-full max-h-[95vh] overflow-y-auto"
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>{initialData ? "Sửa Chi tiết Báo giá" : "Thêm Chi tiết Báo giá"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleFormSubmit} className="space-y-4 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            
            {/* Product selection */}
            <form.Field name="productId">
              {(field) => (
                <div className="space-y-2 col-span-1 md:col-span-2">
                  <SharedCombobox
                    type="product"
                    params={{ customerId }}
                    breadcrumb={["Báo giá", "Chi tiết", "Sản phẩm"]}
                    value={(field.state.value as string) || ""}
                    onChange={(id, selectedObj) => {
                      field.handleChange(id);
                      if (selectedObj) {
                        const orig = selectedObj.original || selectedObj;
                        form.setFieldValue("productCode", orig.productCode || orig.code || "");
                        form.setFieldValue("productName", orig.productName || selectedObj.label || orig.name || "");
                        form.setFieldValue("unit", orig.unit || "Cái");
                        const price = Number(orig.price || orig.costPrice || 0);
                        form.setFieldValue("priceVND", price);
                        form.setFieldValue("isCombo", !!orig.isCombo);

                        const formattedSpecs = formatProductSpecs(orig.specs, orig);
                        if (formattedSpecs && !form.getFieldValue("specs")) {
                          form.setFieldValue("specs", formattedSpecs);
                        }
                      }
                    }}
                    label="Chọn Sản phẩm từ danh mục"
                    placeholder="Tìm kiếm sản phẩm..."
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="productName">
              {(field) => (
                <TanstackFieldWrapper field={field as any} label="Tên Sản phẩm" required className="col-span-1 md:col-span-2">
                  <TanstackFormControl>
                    <Input
                      value={(field.state.value as string) ?? ""}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder="Gõ tên sản phẩm thủ công nếu không có sẵn"
                    />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            </form.Field>

            <form.Field name="specs">
              {(field) => (
                <TanstackFieldWrapper field={field as any} label="Quy cách (Thông tin chi tiết)">
                  <TanstackFormControl>
                    <Textarea
                      value={(field.state.value as string) ?? ""}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder="Kích thước, chất liệu, gia công..."
                      rows={3}
                    />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            </form.Field>

            <form.Field name="notes">
              {(field) => (
                <TanstackFieldWrapper field={field as any} label="Ghi chú">
                  <TanstackFormControl>
                    <Textarea
                      value={(field.state.value as string) ?? ""}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder="Ghi chú thêm..."
                      rows={3}
                    />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            </form.Field>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 col-span-1 md:col-span-2">
              <form.Field name="unit">
                {(field) => (
                  <div className="space-y-1">
                    <Label className="text-xs">ĐVT</Label>
                    <ComboboxConfigMaster
                      categoryCode="UNITS"
                      value={(field.state.value as string) || ""}
                      onChange={(v) => field.handleChange(v)}
                      placeholder="ĐVT"
                    />
                  </div>
                )}
              </form.Field>

              <form.Field name="quantity">
                {(field) => (
                  <TanstackFieldWrapper field={field as any} label="Số lượng" required>
                    <TanstackFormControl>
                      <NumericInput
                        value={(field.state.value as number) ?? 0}
                        onChange={(v) => field.handleChange(v)}
                        placeholder="SL"
                      />
                    </TanstackFormControl>
                  </TanstackFieldWrapper>
                )}
              </form.Field>

              <form.Field name="priceVND">
                {(field) => (
                  <TanstackFieldWrapper field={field as any} label={`Đơn giá bán (${currency})`} required>
                    <TanstackFormControl>
                      <NumericInput
                        value={(field.state.value as number) ?? 0}
                        onChange={(v) => field.handleChange(v)}
                        placeholder="Đơn giá"
                      />
                    </TanstackFormControl>
                  </TanstackFieldWrapper>
                )}
              </form.Field>

              <form.Field name="quotedPrice">
                {(field) => (
                  <TanstackFieldWrapper field={field as any} label={`Giá chốt (${currency})`}>
                    <TanstackFormControl>
                      <NumericInput
                        value={(field.state.value as number) ?? 0}
                        onChange={(v) => field.handleChange(v)}
                        placeholder="Thường bằng Đơn giá bán"
                      />
                    </TanstackFormControl>
                  </TanstackFieldWrapper>
                )}
              </form.Field>
            </div>

            <div className="flex items-center gap-6 col-span-1 md:col-span-2 bg-slate-50 p-4 rounded-lg border border-slate-100 mt-2">
              <form.Field name="isCombo">
                {(field) => (
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={!!field.state.value}
                      onCheckedChange={field.handleChange}
                      id="isCombo"
                    />
                    <Label htmlFor="isCombo" className="cursor-pointer">No MOQ (Gộp Combo)</Label>
                  </div>
                )}
              </form.Field>
              <form.Field name="isCommercial">
                {(field) => (
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={!!field.state.value}
                      onCheckedChange={field.handleChange}
                      id="isCommercial"
                    />
                    <Label htmlFor="isCommercial" className="cursor-pointer">Thương mại (Thuê gia công/Mua ngoài)</Label>
                  </div>
                )}
              </form.Field>
            </div>

            {isCommercialValue && (
              <div className="grid grid-cols-2 gap-4 col-span-1 md:col-span-2">
                <form.Field name="costPrice">
                  {(field) => (
                    <TanstackFieldWrapper field={field as any} label={`Giá mua / Giá thuê ngoài (${currency})`}>
                      <TanstackFormControl>
                        <NumericInput
                          value={(field.state.value as number) ?? 0}
                          onChange={(v) => field.handleChange(v)}
                          placeholder="Chi phí đầu vào..."
                        />
                      </TanstackFormControl>
                    </TanstackFieldWrapper>
                  )}
                </form.Field>
              </div>
            )}
          </div>

          <DialogFooter className="mt-6 border-t pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              <X className="w-4 h-4 mr-2" /> Hủy
            </Button>
            <form.Subscribe selector={(s) => [s.canSubmit, s.isSubmitting]}>
              {([canSubmit, isSubmitting]) => (
                <Button type="submit" disabled={!canSubmit || isSubmitting}>
                  <Save className="w-4 h-4 mr-2" /> Lưu Chi tiết
                </Button>
              )}
            </form.Subscribe>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
